/**
 * Locale-aware formatters used across the app.
 *
 * All read the current i18next language via `i18n.language` so switching the
 * dropdown re-renders correctly-formatted numbers/dates on the next repaint.
 *
 * Because pts and USDT are 1:1 in Clanster (deposits credit `usd_amount` → user
 * balance straight through), `formatUsdCompact` is a display swap only.
 */
import i18n from '../i18n';

const currentLocale = () => (i18n?.language || 'en').split('-')[0];

/** Compact currency (e.g. $1.2K, $48K, $1.4M). Used for 24h Vol pill. */
export function formatUsdCompact(n) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(currentLocale(), {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1,
    }).format(v);
  } catch {
    return `$${Math.round(v)}`;
  }
}

/** Compact plain number (e.g. 4,325 or 4.3K). Used for holders, supply. */
export function formatCountCompact(n) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(currentLocale(), {
      notation: v >= 10_000 ? 'compact' : 'standard',
      maximumFractionDigits: 1,
    }).format(v);
  } catch {
    return String(Math.round(v));
  }
}

/** Regular number with locale grouping (e.g. 1,234.56 or 1 234,56). */
export function formatNumber(n, opts = {}) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(currentLocale(), opts).format(v);
  } catch {
    return String(v);
  }
}

/**
 * Points balance with proper thousands separators (e.g. `301,159.11`).
 * Use everywhere the app shows a pts amount that could exceed 1,000 —
 * balances, portfolio value, lifetime earnings, monthly stash breakdown,
 * per-clan totals, etc. Locale-aware (US uses commas, DE uses dots).
 *
 * @param {number} n         — the raw pts value
 * @param {number} decimals  — max fraction digits (default 2)
 * @returns {string}         — e.g. "301,159.11" or "1,234"
 */
export function formatPts(n, decimals = 2) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(currentLocale(), {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(v);
  } catch {
    return v.toFixed(decimals);
  }
}

/**
 * Integer count with locale grouping (e.g. `44,145` Bits, `800` trades).
 * Zero decimals — for whole quantities like Bits, holders, trade counts.
 */
export function formatCount(n) {
  const v = Number(n) || 0;
  try {
    return new Intl.NumberFormat(currentLocale(), {
      maximumFractionDigits: 0,
    }).format(Math.round(v));
  } catch {
    return String(Math.round(v));
  }
}

/** "3 hours ago" style relative time. `whenIso` is an ISO string or Date. */
export function formatRelativeTime(whenIso) {
  if (!whenIso) return '';
  const when = whenIso instanceof Date ? whenIso : new Date(whenIso);
  if (Number.isNaN(when.getTime())) return '';
  const diffSec = Math.round((when.getTime() - Date.now()) / 1000);
  const abs = Math.abs(diffSec);
  let unit = 'second';
  let value = diffSec;
  if (abs >= 86400) { unit = 'day'; value = Math.round(diffSec / 86400); }
  else if (abs >= 3600) { unit = 'hour'; value = Math.round(diffSec / 3600); }
  else if (abs >= 60) { unit = 'minute'; value = Math.round(diffSec / 60); }
  try {
    return new Intl.RelativeTimeFormat(currentLocale(), { numeric: 'auto' }).format(value, unit);
  } catch {
    return `${Math.abs(value)}${unit[0]} ago`;
  }
}
